import { useGetWorldMapQuery } from "@api/static-assets";
import Icon from "@components/ui/icon";
import { cn } from "@lib/cn";
import { IconList, IconMap2 } from "@tabler/icons-react";
import { registerMap } from "echarts";
import ReactECharts from "echarts-for-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DataByLocation } from "../types";
import { getLocationChartOptions } from "../utils";
import { EmptyStateMessage } from "./empty-state-message";
import { ErrorStateMessage } from "./error-state-message";
import ReadersByLocationList from "./readers-by-location-list";

const ReadersByLocationMap = ({
  data,
  isError,
  view,
  active,
  className,
  isEmpty,
}: {
  data: DataByLocation[];
  isError: boolean;
  active: "readers" | "sessions";
  view?: "version-details" | "session-details" | "widget";
  className?: string;
  isEmpty: boolean;
}) => {
  const isSessionDetails = view === "session-details";

  // Compute center and zoom based on locationData
  const { center, initialZoom, selectedCountryName } = useMemo(() => {
    let computedCenter: [number, number] = [0, 13];
    let computedZoom = 1.24;
    let computedCountryName: string | undefined;
    if (data?.length === 1 && (isSessionDetails || view === "widget")) {
      computedCenter = [data[0].value[0], data[0].value[1]];
      computedZoom = 4; // Zoom in more closely for a single location
      computedCountryName = data[0].country; // For Dublin, set to Ireland
    }
    return {
      center: computedCenter,
      initialZoom: computedZoom,
      selectedCountryName: computedCountryName,
    };
  }, [data, isSessionDetails, view]);

  const chartRef = useRef<ReactECharts>(null);
  const { data: worldMap } = useGetWorldMapQuery({});
  const [registered, setRegistered] = useState(false);
  const [zoom, setZoom] = useState(initialZoom);
  const [showList, setShowList] = useState(false);
  const [prevInitialZoom, setPrevInitialZoom] = useState(initialZoom);

  if (initialZoom !== prevInitialZoom) {
    setPrevInitialZoom(initialZoom);
    setZoom(initialZoom);
  }

  useEffect(() => {
    if (!worldMap) return;
    registerMap("world", worldMap);
    setRegistered(true);
  }, [worldMap]);

  // Handler to update zoom
  const handleZoom = useCallback((delta: number) => {
    setZoom((prev) => {
      const newZoom = Math.max(1.24, Math.min(5, prev + delta));
      // Update ECharts instance directly
      const chartInstance = chartRef.current?.getEchartsInstance();
      if (chartInstance) {
        chartInstance.setOption({
          geo: { zoom: newZoom },
        });
      }
      return newZoom;
    });
  }, []);

  const option = useMemo(() => {
    return getLocationChartOptions(
      center,
      zoom,
      data || [],
      active,
      selectedCountryName,
    );
  }, [center, zoom, data, active, selectedCountryName]);

  return (
    <div
      className={cn(
        "tw-relative tw-flex tw-flex-col tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 tw-bg-white",
        view === "version-details" && "tw-h-[410px]",
        view === "session-details" && "-md:tw-h-[410px]",
        className,
      )}
    >
      <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-5 tw-z-[51]">
        <span className="tw-relative tw-z-[4] tw-flex tw-items-center tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
          <IconMap2 className="tw-size-4 tw-text-blue tw-mr-2" />{" "}
          {active === "readers" ? "Readers" : "Sessions"} by location
        </span>
        <button
          type="button"
          onClick={() => setShowList(!showList)}
          className="tw-px-3.5 tw-text-sm tw-z-10 tw-text-blue tw-shadow-2xs hover:tw-bg-blue-50 tw-transition-colors tw-rounded-lg tw-bg-white tw-outline tw-outline-1 tw-outline-blue/5 tw-font-sans tw-h-9 tw-flex tw-items-center tw-gap-1.5 -sm:tw-p-0 -sm:tw-size-8 -sm:tw-flex -sm:tw-items-center -sm:tw-justify-center"
        >
          {showList ? (
            <>
              <IconMap2 className="tw-size-4 tw-text-blue" />
              <span className="-sm:tw-hidden">Show map</span>
            </>
          ) : (
            <>
              <IconList className="tw-size-4 tw-text-blue" />
              <span className="-sm:tw-hidden">Show list</span>
            </>
          )}
        </button>
      </div>
      {isError && (
        <div className="tw-absolute tw-inset-0 tw-z-[50] tw-flex tw-rounded-lg tw-items-center tw-justify-center tw-bg-white/80">
          <ErrorStateMessage />
        </div>
      )}
      {isEmpty && (
        <div className="tw-absolute tw-inset-0 tw-z-[50] tw-flex tw-rounded-lg tw-items-center tw-justify-center tw-bg-white/80">
          <EmptyStateMessage
            title="No analytics yet, but you're on the right track"
            description="Share your Storydocs to start collecting insights. We’ll show your data as soon as it's ready."
            className="tw-w-full tw-max-w-80"
          />
        </div>
      )}
      {showList ? (
        <ReadersByLocationList data={data} active={active} />
      ) : (
        <div className="tw-relative tw-z-[9] tw-grow">
          <div className="tw-absolute tw-right-7 tw-top-8 tw-z-50 tw-flex tw-overflow-hidden tw-flex-col tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue/5 tw-bg-white -sm:tw-right-5 -sm:tw-top-2">
            <button
              className="tw-bg-transparent tw-flex tw-size-8 tw-items-center tw-justify-center tw-border-none tw-outline-none tw-transition-colors tw-duration-200 hover:tw-bg-blue-50"
              onClick={() => handleZoom(0.2)}
              aria-label="Zoom in"
              type="button"
            >
              <Icon icon="ri-add-line" />
            </button>
            <span className="tw-block tw-h-px tw-w-full tw-bg-blue/5"></span>
            <button
              className="tw-bg-transparent tw-flex tw-size-8 tw-items-center tw-justify-center tw-border-none tw-outline-none tw-transition-colors tw-duration-200 hover:tw-bg-blue-50"
              onClick={() => handleZoom(-0.2)}
              aria-label="Zoom out"
              type="button"
            >
              <Icon icon="ri-subtract-line" />
            </button>
          </div>
          {registered && (
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ height: "100%", width: "100%" }}
              opts={{ renderer: "canvas" }}
              notMerge={false}
              lazyUpdate={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ReadersByLocationMap;
